import { DataStore, SortDirection } from "aws-amplify";
import { useDispatch } from "react-redux";
import { setListing, setSelected } from "../store/ducks/order";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import { Order } from "../models";
import { CreateOrderInput } from "../models/api";
import {
  CreateVariables,
  ListingVariables,
  OrderBulkTrashVariables,
  GetVariables,
  UpdateVariables,
  Option,
} from "../models/app";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  async function fetch(params: ListingVariables) {
    const { searchText, startIndex, limit } = params;

    try {
      const listing = await DataStore.query(
        Order as any,
        (model: any) => {
          model.deleted("eq", "0");

          if (searchText.length > 0)
            model.checkNumber("contains", searchText.toLowerCase());

          return model;
        },
        {
          page: startIndex / limit,
          limit: limit,
          sort: (s) => s.updatedAt(SortDirection.DESCENDING),
        }
      );

      console.log({ listing });
      return listing;
    } catch (err) {
      showError(err);
    }
  }

  async function get(params: GetVariables) {
    const { id } = params;

    try {
      const single: Order | undefined = await DataStore.query(Order as any, id);

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;

    try {
      const createInput: CreateOrderInput = {
        symphonyID: data.symphonyID.toLowerCase(),
        checkNumber: data.checkNumber.toLowerCase(),
        status: data.status,
        reason: data.reason,
        totalAmount: parseInt(data.totalAmount),
        tenderId: parseInt(data.tenderId),
        paymentType: data.paymentType,
        orderTypeId: parseInt(data.orderTypeId),
        orderType: data.orderType,
        employeeId: parseInt(data.employeeId),
        rvcId: parseInt(data.rvcId),
        deleted: "0",
        createdAt: new Date().toLocaleString(),
        createdByID: userID,
        createdByName: userName,
      };

      const model = await DataStore.save(new Order(createInput as any));

      showConfirm(`New ${singleName} has been created successfully`);
      return model;
    } catch (err) {
      showError(err);
    }
  }

  async function update(params: UpdateVariables) {
    const { id, data } = params;

    try {
      const original = await get({ id });

      await DataStore.save(
        Order.copyOf(original!, (updated) => {
          updated.symphonyID = data.symphonyID
            ? data.symphonyID.toLowerCase()
            : original!.symphonyID;
          updated.checkNumber = data.checkNumber
            ? data.checkNumber.toLowerCase()
            : original!.checkNumber;
          updated.status = data.status ? data.status : original!.status;
          updated.totalAmount = data.totalAmount
            ? data.totalAmount
            : original!.totalAmount;
        })
      );

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: GetVariables) {
    try {
      const original = await get(params);

      await DataStore.save(
        Order.copyOf(original!, (updated) => {
          updated.deleted = "1";
        })
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(params: OrderBulkTrashVariables) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err: Error | any) {
        throw err;
      }
    });

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: GetVariables) {
    const { id } = params;

    try {
      await DataStore.delete(id as any);

      // dispatch(setListing(listing.filter((model: any) => model.id !== id)));

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  async function exportAll(params: ListingVariables) {
    const data = await fetch(params);

    let exportedData = [];

    for (let order of data!) {
      let row: any = { ...order };
      exportedData.push(row);
    }

    return exportedData;
  }

  function options(listing: Order[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({ label: option.symphonyID!, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "checkNumber",
      numeric: false,
      disablePadding: false,
      label: "Check Number",
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
    },
    {
      id: "reason",
      numeric: false,
      disablePadding: false,
      label: "Reason",
    },
    {
      id: "paymentType",
      numeric: false,
      disablePadding: false,
      label: "Payment Type",
    },
    {
      id: "totalAmount",
      numeric: false,
      disablePadding: false,
      label: "Total Amount",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "checkNumber",
    "status",
    "reason",
    "paymentType",
    "totalAmount",
  ];

  const api: any = {};

  api[`${listingName}Model`] = Order as any;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: Order[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (id: string) =>
    dispatch(setSelected(id));
  api[`${listingName}Export`] = exportAll;

  return api;
};

export default useResource;
